import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canRunModpackUpdates } from '../utils/modpackConfigPermissions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { updateAdvisorApiService } from '../services/updateAdvisorApi';
import { UpdateAdvisorEntry, UpdateAdvisorResponse } from '../types/updateAdvisor';
import { advisorIsStale, advisorStatus, advisorPackStatus, advisorCheckMessage, advisorProjectUrl } from '../utils/updateAdvisorStatus';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { useDeferredMinecraftServers } from '../hooks/useDeferredMinecraftServers';
import type { MinecraftServersData } from '../types/statusMonitor';
import {
  formatBackupAgeCompact,
  getBackupAgeSeverity,
  getRegionBackupLine,
  instancesForAdvisorCluster,
} from '../utils/minecraftBackupStatus';
import {
  Package,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  CheckCircle2,
  ArrowUpCircle,
  MapPin,
  PackageCheck,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';

/** Group entries by cluster_parent; each group = one unique modpack across regions */
function groupByClusterParent(entries: UpdateAdvisorEntry[]): Map<number, UpdateAdvisorEntry[]> {
  const map = new Map<number, UpdateAdvisorEntry[]>();
  for (const entry of entries) {
    const key = entry.cluster_parent;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(entry);
  }
  return map;
}

const getModloaderColor = (modloader: string) => {
  switch ((modloader || '').toLowerCase()) {
    case 'forge':
      return 'text-purple-400';
    case 'neoforge':
      return 'text-indigo-400';
    case 'fabric':
      return 'text-fuchsia-400';
    default:
      return 'text-gray-400';
  }
};

interface PackCardProps {
  entries: UpdateAdvisorEntry[];
  index: number;
  canUpdate: boolean;
  /** Only passed for pending-update cards (live backup row). */
  minecraftServers?: MinecraftServersData;
  /** Bumps periodically so age labels refresh without new WebSocket messages. */
  backupClock?: number;
}

function regionBackupMotionKey(line: ReturnType<typeof getRegionBackupLine>): string {
  if (line.kind === 'age') {
    const bucket = Math.floor((line.msSinceBackup ?? 0) / 30_000);
    return `age-${bucket}`;
  }
  return line.kind;
}

function PendingRegionBackupBadges({
  entries,
  minecraftServers,
  backupClock,
}: {
  entries: UpdateAdvisorEntry[];
  minecraftServers: MinecraftServersData;
  backupClock: number;
}) {
  void backupClock;
  const rep = entries[0];
  const clusterHasSocketData =
    !!rep && instancesForAdvisorCluster(rep, minecraftServers).length > 0;

  const sorted = useMemo(
    () => [...entries].sort((a, b) => a.region_name.localeCompare(b.region_name)),
    [entries]
  );

  const now = Date.now();

  const regionBadge = (label: string) => (
    <span className="chip justify-center min-w-[1.75rem] font-semibold tracking-wide shrink-0">
      {label}
    </span>
  );

  return (
    <div className="mt-2 pt-2 border-t border-glass-200/10">
      <div className="eyebrow mb-1">Backup by region</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
        {sorted.map((entry) => {
          const line = getRegionBackupLine(entry, minecraftServers, clusterHasSocketData, now);
          const label = line.regionLabel;
          const mk = regionBackupMotionKey(line);

          const statusNode = (() => {
            switch (line.kind) {
              case 'loading':
                return (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-glass-400">
                    <span className="inline-block h-1 w-1 rounded-full bg-glass-400/80 animate-pulse" />
                    …
                  </span>
                );
              case 'no_host':
                return (
                  <span className="text-[11px] font-medium text-glass-500" title="No status data for this cluster">
                    —
                  </span>
                );
              case 'offline':
                return (
                  <span className="text-[11px] font-medium text-glass-500" title="Instance offline in status monitor">
                    offline
                  </span>
                );
              case 'in_progress':
                return (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-400/90">
                    <RefreshCw className="w-2.5 h-2.5 animate-spin shrink-0 opacity-80" />
                    In progress
                  </span>
                );
              case 'na':
                return <span className="text-[11px] font-medium text-glass-500">N/A</span>;
              case 'age': {
                const ms = line.msSinceBackup ?? 0;
                const sev = getBackupAgeSeverity(ms);
                const ageText = formatBackupAgeCompact(ms);
                const valueColor =
                  sev === 'critical'
                    ? 'text-red-400'
                    : sev === 'concern'
                      ? 'text-yellow-400'
                      : 'text-glass-300';
                return (
                  <span className={cn('text-[11px] font-medium tabular-nums', valueColor)} title="Time since last backup">
                    {ageText} ago
                  </span>
                );
              }
            }
          })();

          return (
            <motion.div
              key={entry.id}
              layout
              className="inline-flex items-center gap-1.5 min-w-0 max-w-full"
              title={line.kind === 'age' ? `Last backup ${formatBackupAgeCompact(line.msSinceBackup ?? 0)} ago` : undefined}
            >
              {regionBadge(label)}
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={`${entry.id}-${mk}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                  className="min-w-0"
                >
                  {statusNode}
                </motion.span>
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

const PackCard = ({
  entries,
  index,
  canUpdate,
  minecraftServers = {},
  backupClock = 0,
}: PackCardProps) => {
  const rep = entries.find(entry => advisorStatus(entry) === 'update_available') || entries[0];
  const status = advisorPackStatus(entries);
  const hasUpdate = status === 'update_available';
  const unknown = status === 'unknown';
  const projectUrl = advisorProjectUrl(rep);
  const updates = entries.filter(entry => advisorStatus(entry) === 'update_available');
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.25 }}
      className={cn(
        'glass-card p-4 flex items-center gap-4',
        (hasUpdate || unknown) && 'border-amber-500/40'
      )}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-dark-800/60 border border-glass-200/10">
        <img
          src={rep.thumbnail_url}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'https://cdn.playcdu.co/Images/Branding/Blue/BH_NU_Asset_6.png';
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {projectUrl ? <a href={projectUrl} target="_blank" rel="noopener noreferrer"
            className="text-sm font-semibold text-white hover:text-accent-300 truncate"
            aria-label={`Open download for ${rep.modpack_full_name}`}>{rep.modpack_full_name}</a>
            : <span className="text-sm font-semibold text-white truncate">{rep.modpack_full_name}</span>}
          {hasUpdate && (
            <span className="badge badge-amber">
              <ArrowUpCircle className="w-3 h-3" />
              Update
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs">
          <span className={cn('font-medium', getModloaderColor(rep.modloader))}>
            {rep.modloader}
          </span>
          {rep.latest_version && <span className="text-glass-400">Latest release: {rep.latest_version}</span>}
        </div>
        <div className="mt-2 space-y-1" aria-label="Installed versions by region">
          {entries.map(entry => {
            const state = advisorStatus(entry);
            return <div key={entry.server_address} className="flex items-center gap-2 text-xs" title={entry.server_address}>
              <span className="chip"><MapPin className="w-2.5 h-2.5" />{entry.region_name || entry.server_address}</span>
              <span className="text-glass-300 tabular-nums">{entry.modpack_version || 'Unknown version'}</span>
              <span className={state === 'up_to_date' ? 'text-emerald-400' : 'text-amber-300'}>
                {state === 'update_available' ? `Update to ${entry.latest_version}` : state === 'up_to_date' ? 'Up to date' : advisorCheckMessage(entry.error)}
              </span>
            </div>;
          })}
        </div>
        {hasUpdate && entries.length > 0 && (
          <PendingRegionBackupBadges
            entries={entries}
            minecraftServers={minecraftServers}
            backupClock={backupClock}
          />
        )}
        {canUpdate && hasUpdate && <div className="mt-3 flex flex-wrap gap-2" aria-label={`Guided updates for ${rep.modpack_full_name}`}>
          {updates.map(entry => <Link key={entry.id}
            to={`/modpack-configs/${encodeURIComponent(entry.server_address)}?section=updates`}
            className="glass-button-sm text-xs text-accent-300"
            title={`Update ${entry.server_address}`}>
            <PackageCheck className="w-3.5 h-3.5 shrink-0" />
            {entries.length === 1 ? 'Update pack' : `Update · ${entry.region_name || entry.server_address}`}
          </Link>)}
        </div>}
      </div>

      {/* Status & link */}
      <div className="flex-shrink-0 flex items-center gap-2">
        {hasUpdate ? (
          <ArrowUpCircle className="w-5 h-5 text-amber-400" />
        ) : unknown ? (
          <AlertCircle className="w-5 h-5 text-amber-400" aria-label="Check unavailable" />
        ) : (
          <CheckCircle2 className="w-5 h-5 text-emerald-500/80" />
        )}
        {projectUrl && <a href={projectUrl} target="_blank" rel="noopener noreferrer"
          aria-label={`Open provider page for ${rep.modpack_full_name}`}
          className="text-glass-400 hover:text-accent-300 transition-colors"><ExternalLink className="w-4 h-4" /></a>}
      </div>
    </motion.div>
  );
};

export const UpdateAdvisorView = () => {
  useDocumentTitle('Update Advisor');
  const { identity } = useAuth();
  const canUpdate = canRunModpackUpdates(identity);

  const [response, setResponse] = useState<UpdateAdvisorResponse | null>(null);
  const [clock, setClock] = useState(Date.now());
  const inFlight = useRef<AbortController | null>(null);
  const stale = response ? advisorIsStale(response, clock) : false;
  const data = useMemo(() => response ? response.data.map(entry => stale
    ? { ...entry, update_status: 'unknown' as const, update_available: false, error: 'advisory_stale' }
    : entry) : null, [response, stale]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    if (inFlight.current) return;
    const controller = new AbortController();
    inFlight.current = controller;
    const silent = opts?.silent === true;
    if (silent) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await updateAdvisorApiService.getPacks(controller.signal);
      if (!controller.signal.aborted) { setResponse(res); setClock(Date.now()); }
    } catch (err) {
      if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Failed to load update advisor data');
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
        setIsRefreshing(false);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchData();
    const refresh = () => {
      setClock(Date.now());
      if (document.visibilityState === 'visible') void fetchData({ silent: true });
    };
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      inFlight.current?.abort();
      inFlight.current = null;
    };
  }, [fetchData]);

  const { pendingPacks, upToDatePacks, unknownPacks } = useMemo(() => {
    if (!data) return { pendingPacks: [], upToDatePacks: [], unknownPacks: [] };
    const groups = groupByClusterParent(data);
    const pending: [number, UpdateAdvisorEntry[]][] = [];
    const upToDate: [number, UpdateAdvisorEntry[]][] = [];
    const unknown: [number, UpdateAdvisorEntry[]][] = [];
    for (const [clusterParent, entries] of groups) {
      const status = advisorPackStatus(entries);
      if (status === 'update_available') {
        pending.push([clusterParent, entries]);
      } else if (status === 'up_to_date') {
        upToDate.push([clusterParent, entries]);
      } else {
        unknown.push([clusterParent, entries]);
      }
    }
    return {
      pendingPacks: pending,
      upToDatePacks: upToDate,
      unknownPacks: unknown,
    };
  }, [data]);

  /** Full-page skeleton only on first load; silent refresh keeps the list mounted so backup UI stays stable. */
  const mainListReady = !loading && data !== null;
  const minecraftServers = useDeferredMinecraftServers(mainListReady);

  const [backupClock, setBackupClock] = useState(0);
  useEffect(() => {
    if (pendingPacks.length === 0) return;
    const id = window.setInterval(() => {
      setBackupClock((c) => c + 1);
    }, 30_000);
    return () => window.clearInterval(id);
  }, [pendingPacks.length]);

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <PageHeader
        eyebrow="Infrastructure"
        title="Update Advisor"
        icon={Package}
        description="Modpack version status across the network — prioritize packs with pending updates"
        actions={
          <button
            onClick={() => void fetchData({ silent: true })}
            disabled={loading || isRefreshing}
            className="glass-button"
            title="Refresh data"
          >
            <RefreshCw className={cn('w-4 h-4', (loading || isRefreshing) && 'animate-spin')} />
            <span>Refresh</span>
          </button>
        }
      />

      {/* Error state */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert alert-danger"
        >
          <AlertCircle />
          <span className="flex-1 min-w-0">{error}</span>
          <button
            onClick={() => void fetchData({ silent: data !== null })}
            className="glass-button-secondary btn-xs ml-auto shrink-0"
          >
            Retry
          </button>
        </motion.div>
      )}

      {response && <div className="space-y-2 text-sm text-glass-400" aria-live="polite">
        <p>Releases checked: {new Date(response.timestamp * 1000).toLocaleString()}. The page refreshes every minute.</p>
        {(stale || response.refresh_error || response.refreshing) && <div className="alert alert-warning">
          <AlertCircle />
          <span>{stale ? 'Release checks are overdue. These packs cannot be confirmed up to date.'
            : response.refresh_error ? 'The latest refresh failed. Showing the last successful release checks.'
            : 'Checking for new releases...'}</span>
        </div>}
      </div>}

      {/* Summary stats */}
      {!loading && data && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <StatTile
            label="Pending Updates"
            icon={ArrowUpCircle}
            tone="amber"
            value={<span className="text-amber-400">{pendingPacks.length}</span>}
          />
          <StatTile
            label="Up to Date"
            icon={CheckCircle2}
            tone="success"
            value={<span className="text-emerald-400">{upToDatePacks.length}</span>}
          />
          <StatTile
            label="Server Instances"
            icon={Package}
            tone="accent"
            value={data.length}
            hint="across all regions"
          />
          <StatTile
            label="Checks Unavailable"
            icon={AlertCircle}
            tone="amber"
            value={unknownPacks.length}
            hint="not confirmed up to date"
          />
        </motion.div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-4" aria-busy="true">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <StatTile key={i} label="" value="" loading />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="glass-card p-4 flex items-center gap-4">
                <SkeletonLoader className="w-14 h-14 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <SkeletonLoader className="h-4 w-3/4" />
                  <SkeletonLoader className="h-3 w-1/2" />
                  <SkeletonLoader className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pack lists */}
      {!loading && data && (
        <motion.div
          className="space-y-6"
          animate={{ opacity: isRefreshing ? 0.88 : 1 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          {/* Pending updates — prioritized at top */}
          {pendingPacks.length > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <SectionHeader
                as="h2"
                icon={ArrowUpCircle}
                iconClassName="text-amber-400"
                title="Packs with Pending Updates"
                count={`${pendingPacks.length} pack${pendingPacks.length !== 1 ? 's' : ''}`}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <AnimatePresence mode="popLayout">
                  {pendingPacks.map(([clusterParent, entries], i) => (
                    <PackCard
                      key={clusterParent}
                      entries={entries}
                      index={i}
                      canUpdate={canUpdate}
                      minecraftServers={minecraftServers}
                      backupClock={backupClock}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </motion.section>
          )}

          {unknownPacks.length > 0 && <section>
            <SectionHeader as="h2" icon={AlertCircle} iconClassName="text-amber-400"
              title="Checks Unavailable" count={`${unknownPacks.length} packs`} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {unknownPacks.map(([clusterParent, entries], i) => <PackCard key={clusterParent} entries={entries} index={i} canUpdate={canUpdate} />)}
            </div>
          </section>}

          {/* Up to date */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <SectionHeader
              as="h2"
              icon={CheckCircle2}
              iconClassName="text-emerald-500"
              title="Up to Date"
              count={`${upToDatePacks.length} pack${upToDatePacks.length !== 1 ? 's' : ''}`}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <AnimatePresence mode="popLayout">
                {upToDatePacks.map(([clusterParent, entries], i) => (
                  <PackCard key={clusterParent} entries={entries} index={pendingPacks.length + i} canUpdate={canUpdate} />
                ))}
              </AnimatePresence>
            </div>
          </motion.section>
        </motion.div>
      )}

      {/* Empty / no data */}
      {!loading && !error && data && data.length === 0 && (
        <EmptyState icon={Package} title="No modpack data available." />
      )}
    </div>
  );
};
